import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, FileText, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

export default function Events() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="events-page">
      <SEO title="Soirées & événements" description="Soirées Fado, fêtes traditionnelles portugaises (São João), brunchs dominicaux : retrouvez l'agenda festif du Globo Dancing à Trappes." path="/evenements"/>
      <section className="pt-32 pb-12 azulejo-bg">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 reveal">
          <div className="eyebrow">Festas e tradições</div>
          <h1 className="text-5xl md:text-7xl mt-4 leading-[0.95]">
            Nos <em className="italic text-[#C04E38]">soirées</em>
          </h1>
          <p className="text-[#5C5A56] text-lg mt-5 max-w-2xl">
            Du Fado intime aux grandes fêtes traditionnelles, la culture
            portugaise se vit ici en famille.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          {events.length === 0 && (
            <p className="text-center text-[#5C5A56]" data-testid="events-empty">Aucun événement à venir pour l'instant.</p>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((e) => {
              const posterIsPdf = e.poster_url?.toLowerCase().endsWith(".pdf");
              return (
                <article key={e.id} className="card-warm overflow-hidden flex flex-col" data-testid={`event-${e.id}`}>
                  {e.image_url && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={e.image_url} alt={e.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex flex-wrap gap-3 text-xs uppercase tracking-widest text-[#C04E38] mb-3">
                      <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {new Date(e.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
                      {e.time && <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {e.time}</span>}
                    </div>
                    <h3 className="text-2xl mb-2">{e.title}</h3>
                    <p className="text-[#5C5A56] leading-relaxed">{e.description}</p>

                    {/* Special menu */}
                    {e.special_menu?.length > 0 && (
                      <div className="mt-5 pt-5 border-t border-[#E6E1D6]" data-testid={`event-menu-${e.id}`}>
                        <div className="text-xs uppercase tracking-widest text-[#1F476B] font-medium mb-3">Menu spécial de la soirée</div>
                        <ul className="space-y-2.5">
                          {e.special_menu.map((it, i) => (
                            <li key={i} className="flex gap-2">
                              <div className="flex-1">
                                <div className="flex items-baseline gap-2">
                                  <span className="font-serif-display text-[15px]">{it.name}</span>
                                  <div className="flex-1 border-b border-dotted border-[#5C5A56]/30 mb-1" />
                                  {it.price && <span className="font-serif-display text-sm text-[#C04E38] whitespace-nowrap">{it.price}</span>}
                                </div>
                                {it.description && <p className="text-xs text-[#5C5A56] mt-0.5">{it.description}</p>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Poster */}
                    {e.poster_url && (
                      <a href={e.poster_url} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm text-[#1F476B] hover:text-[#C04E38] transition" data-testid={`event-poster-${e.id}`}>
                        {posterIsPdf ? <FileText size={14}/> : <ExternalLink size={14}/>}
                        {posterIsPdf ? "Télécharger l'affiche (PDF)" : "Voir l'affiche"}
                      </a>
                    )}

                    <Link to="/reserver" className="inline-flex items-center gap-2 mt-5 text-[#C04E38] hover:text-[#A63D2A] transition font-medium" data-testid={`reserve-event-${e.id}`}>
                      Réserver pour cette soirée <ArrowRight size={14} />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
